import React, {createContext, useContext, useMemo, useState} from 'react';

import springboard from 'springboard';

import {UploadFileModal} from './upload_file_modal';
import {TextInputModal} from './text_input_modal';
import {useModule} from '@/hooks/useModule';
import {CustomModal} from './custom_modal';
import {ConfirmationModal} from './confirmation_modal';

export interface AllModals {
    UploadFileModal: typeof UploadFileModal;
    TextInputModal: typeof TextInputModal;
    CustomModal: typeof CustomModal;
    ConfirmationModal: typeof ConfirmationModal;
}

type OpenModalFunc = <ModalName extends keyof AllModals>(
    modalName: ModalName,
    props: React.ComponentProps<AllModals[ModalName]>['data'],
) => void;

type ModalsProviderContextValue = {
    openModal: OpenModalFunc;
    closeModal: () => void;
}

const modalsContext = createContext<ModalsProviderContextValue>(null as any);

type ModalProviderState = {name: keyof AllModals; props: object | undefined} | null;

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        Modals: ModalsModuleReturnValue;
    }
}

type ModalsModuleReturnValue = {
    registerModal: <ModalName extends keyof AllModals>(
        name: ModalName,
        component: AllModals[ModalName],
    ) => void
    modals: AllModals;
};

springboard.registerModule('Modals', {}, async (moduleAPI): Promise<ModalsModuleReturnValue> => {
    const modals = {
        UploadFileModal,
        TextInputModal,
        CustomModal,
        ConfirmationModal,
    } as AllModals;

    const registerModal = <ModalName extends keyof AllModals>(
        name: ModalName,
        component: AllModals[ModalName],
    ) => {
        (modals as any)[name] = component;
    };

    return {
        registerModal,
        modals,
    };
});

export const ModalsProvider = (props: React.PropsWithChildren) => {
    const [modalState, setModalState] = useState<ModalProviderState>(null);

    const modalsModule = useModule('Modals');

    const value: ModalsProviderContextValue = useMemo(() => ({
        openModal: (name, props) => {
            setModalState({name, props});
        },
        closeModal: () => setModalState(null),
    }), []);

    const registeredModals = modalsModule.modals;
    const modals = useMemo(() => {
        const modals = Object.keys(registeredModals).map((name) => {
            const Component = registeredModals[name as keyof AllModals];
            const modalProps = modalState?.name === name ? (modalState.props as React.ComponentProps<typeof Component>['data']) : undefined;
            return (
                <Component
                    key={name}
                    data={modalProps as any}
                    close={() => value.closeModal()}
                />
            );
        });

        return modals;
    }, [modalState, value]);

    return (
        <modalsContext.Provider value={value}>
            {modals}
            {props.children}
        </modalsContext.Provider>
    );
};

export const useModals = () => {
    const providerValue = useContext(modalsContext);
    return providerValue;
};
